import { useEffect, useMemo, useState } from "react"
import AlertSetting from "../AlertSetting"
import Toggle from "../shared/Toggle"
import Actions from "@/components/AlertSetting/Actions"
import { IconColor, IconProps } from "@/components/shared/Icon"
import Tooltip, { TooltipPosition } from "@/components/shared/Tooltip"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { AlertSource } from "@/types/alert"
import "./index.scss"

export type DeviceTypeForm = {
   wearables: boolean
   sensors: boolean
}

export interface AlertEnabledSettingsProps {
   title: string
   iconName: IconProps["name"]
   form: DeviceTypeForm
   onSave: (sensorTypes: AlertSource[]) => void
}

const AlertEnabledSettings = ({
   title,
   iconName,
   form,
   onSave,
}: AlertEnabledSettingsProps) => {
   const initialForm = useMemo(() => {
      return { ...form }
   }, [form])

   const [isEditing, setIsEditing] = useState(false)
   const [formState, setFormState] = useFormStateHandler(form)

   useEffect(() => {
      setFormState(form)
   }, [form, setFormState])

   const isFormChanged = useMemo(() => {
      const keys = Object.keys(initialForm) as (keyof typeof initialForm)[]
      return keys.some((key) => initialForm[key] !== formState[key])
   }, [initialForm, formState])

   const handleCancel = () => {
      setFormState(form)
      setIsEditing(false)
   }

   const handleSave = () => {
      const sensorTypes: AlertSource[] = []
      if (formState.wearables) sensorTypes.push(AlertSource.WEARABLE)
      if (formState.sensors) sensorTypes.push(AlertSource.STATIC)
      onSave(sensorTypes)
      setIsEditing(false)
   }

   const handleChange = (key: keyof DeviceTypeForm) => {
      setFormState((prev) => ({ ...prev, [key]: !prev[key] }))
   }

   return (
      <AlertSetting
         iconColor={IconColor.HONOLULU_BLUE}
         iconName={iconName}
         title={title}
      >
         <div className="alert-enabled-settings">
            <div className="alert-enabled-settings__title">
               <Actions
                  isDisabled={
                     !isFormChanged ||
                     (!formState.wearables && !formState.sensors)
                  }
                  isEditing={isEditing}
                  onCancel={handleCancel}
                  onEdit={() => setIsEditing(true)}
                  onSave={handleSave}
               />
            </div>
            <div className="alert-enabled-settings__items">
               <Tooltip
                  content="At least one device needs to be enabled"
                  open={isEditing && !formState.wearables && !formState.sensors}
                  position={TooltipPosition.RIGHT}
                  classes={{
                     tooltip: "alert-enabled-settings__tooltip-sensors",
                  }}
               >
                  <Toggle
                     label="Cell sensors"
                     value={formState.sensors}
                     disabled={!isEditing}
                     onChange={() => handleChange("sensors")}
                     className="alert-enabled-settings__toggle"
                  />
               </Tooltip>
               <Tooltip
                  content="At least one device needs to be enabled"
                  open={isEditing && !formState.wearables && !formState.sensors}
                  position={TooltipPosition.RIGHT}
                  classes={{
                     tooltip: "alert-enabled-settings__tooltip-wearables",
                  }}
               >
                  <Toggle
                     label="ReassureBands"
                     value={formState.wearables}
                     disabled={!isEditing}
                     onChange={() => handleChange("wearables")}
                     className="alert-enabled-settings__toggle"
                  />
               </Tooltip>
            </div>
         </div>
      </AlertSetting>
   )
}

export default AlertEnabledSettings
